import { Box, Button, Container, Group, SimpleGrid, Stack, Tabs, Text, Title } from "@mantine/core";
import { useEffect, useState } from "react";
import { IconEye } from "@tabler/icons-react";
import { ArticleInfoForm } from "@components/blog/ArticleInfoForm";
import { AddSectionForm } from "@components/blog/AddSectionForm";
import { SectionList } from "@components/blog/SectionList";
import { BlogPreview } from "@components/blog/BlogPreview";
import { createBlogPost, uploadBlogImage } from "@utils/api-communication/RestApiCli";
import type { ContentBlock } from "../../types/blogTypes";

export default function BlogEditor() {
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [tags, setTags] = useState("");
  const [featuredImageFile, setFeaturedImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState("");
  const [sections, setSections] = useState<ContentBlock[]>([]);
  const [activeTab, setActiveTab] = useState<string | null>("editor");
  const [isPosting, setIsPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const [postSuccess, setPostSuccess] = useState(false);

  useEffect(() => {
    if (!featuredImageFile) {
      setImagePreview("");
      return;
    }

    const url = URL.createObjectURL(featuredImageFile);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [featuredImageFile]);

  const handleAddSection = (section: {
    type: "title" | "paragraph" | "numbered" | "references";
    text?: string;
    items?: Array<
      | { number: number; title?: string; text: string }
      | { number: number; text: string; link?: string }
    >;
  }) => {
    setSections((prev) => [...prev, section as ContentBlock]);
  };

  const handleRemoveSection = (index: number) => {
    setSections((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleMoveSection = (index: number, direction: "up" | "down") => {
    setSections((prev) => {
      if (direction === "up" && index > 0) {
        const newSections = [...prev];
        [newSections[index], newSections[index - 1]] = [newSections[index - 1], newSections[index]];
        return newSections;
      }
      if (direction === "down" && index < prev.length - 1) {
        const newSections = [...prev];
        [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
        return newSections;
      }
      return prev;
    });
  };

  const handlePostArticle = async () => {
    if (!title.trim()) {
      setPostError("Title is required");
      return;
    }

    setIsPosting(true);
    setPostError(null);
    setPostSuccess(false);

    const slug = title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

    const payload: any = {
      title: title.trim(),
      slug,
      excerpt: excerpt.trim(),
      content: sections,
      author: author.trim() || null,
      status: "published",
      tags: tags
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0),
    };

    try {
      if (imagePreview && featuredImageFile) {
        const imageUrl = await uploadBlogImage(featuredImageFile, slug);
        if (!imageUrl) {
          setPostError("Image upload failed. Please try again.");
          setIsPosting(false);
          return;
        }
        payload.featuredImage = imageUrl;
      }

      await createBlogPost(payload);

      setPostSuccess(true);
      setTitle("");
      setAuthor("");
      setExcerpt("");
      setTags("");
      setSections([]);
      setFeaturedImageFile(null);
      setImagePreview("");
      setIsPosting(false);
    } catch (error) {
      setPostError(error instanceof Error ? error.message : "An error occurred");
      setIsPosting(false);
    }
  };

  return (
    <>
      <Container size="xl" py="xl" mt="70px">
        <Group justify="space-between" align="center" mb="xl">
          <Title order={1}>Blog Editor</Title>
          <Button onClick={handlePostArticle} loading={isPosting} disabled={!title.trim()}>
            Post Article
          </Button>
        </Group>

        {postSuccess && (
          <Box
            mb="md"
            p="md"
            style={{
              backgroundColor: "var(--mantine-color-green-0)",
              borderRadius: "var(--mantine-radius-md)",
              border: "1px solid var(--mantine-color-green-3)",
            }}
          >
            <Text c="green">Article posted successfully!</Text>
          </Box>
        )}

        {postError && (
          <Box
            mb="md"
            p="md"
            style={{
              backgroundColor: "var(--mantine-color-red-0)",
              borderRadius: "var(--mantine-radius-md)",
              border: "1px solid var(--mantine-color-red-3)",
            }}
          >
            <Text c="red">{postError}</Text>
          </Box>
        )}

        <Tabs value={activeTab} onChange={setActiveTab} defaultValue="editor">
          <Tabs.List>
            <Tabs.Tab value="editor">Editor</Tabs.Tab>
            <Tabs.Tab value="preview" leftSection={<IconEye size={16} />}>
              Preview
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="editor" pt="xl">
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" style={{ alignItems: "flex-start" }}>
              <Stack gap="lg">
                <ArticleInfoForm
                  title={title}
                  author={author}
                  excerpt={excerpt}
                  tags={tags}
                  featuredImageFile={featuredImageFile}
                  imagePreview={imagePreview}
                  onTitleChange={setTitle}
                  onAuthorChange={setAuthor}
                  onExcerptChange={setExcerpt}
                  onTagsChange={setTags}
                  onFeaturedImageChange={setFeaturedImageFile}
                />
                <AddSectionForm onAddSection={handleAddSection} />
              </Stack>
              <Stack gap="lg">
                <SectionList
                  sections={sections}
                  onRemove={handleRemoveSection}
                  onMove={handleMoveSection}
                />
              </Stack>
            </SimpleGrid>
          </Tabs.Panel>

          <Tabs.Panel value="preview" pt="xl">
            <BlogPreview
              title={title}
              author={author}
              excerpt={excerpt}
              tags={tags}
              imagePreview={imagePreview}
              sections={sections}
            />
          </Tabs.Panel>
        </Tabs>
      </Container>
    </>
  );
}
